import type { Time } from "@suse-o11y/date-time/time";
import type { Signal } from "@suse-o11y/signal";
import React from "react";
import { useSelector } from "react-redux";

import { Truncated } from "~/components/truncated";

import { selectTimeRange, selectTimestampDate } from "../../../../../../app/state/view/timeline/TimelineSelectors";
import { Widget, WidgetBody, WidgetHeader } from "../../../../../../components/_widget";
import { ComponentLinkWithType } from "../../../../../../components/react/component-link/ComponentLinkWithType";

export interface ComponentReference {
	identifier: string;
	type: string;
	name: string;
	timestamp?: Time;
}

export interface PinnedItemWidgetProps {
	isExpanded: Signal<boolean>;
	title: undefined | string;
	description: undefined | string;
	component: undefined | ComponentReference;
	actions: React.ReactNode;
	children: React.ReactNode;
}

export const PinnedItemWidget: React.FC<PinnedItemWidgetProps> = ({
	isExpanded,
	title,
	description,
	component,
	actions,
	children,
}) => {
	const timeRange = useSelector(selectTimeRange);
	const timestamp = useSelector(selectTimestampDate);

	return (
		<Widget
			card
			data-testid="pinned-item"
			divided
			isExpanded={isExpanded}
			namespace={["pinned-card", "pinned-items"]}
			py="sm"
		>
			<WidgetHeader
				className="sticky top-14 z-10 bg-white"
				description={
					component && (
						<ComponentLinkWithType
							className="text-13/[21px] text-grey-600"
							data-testid="card-subtitle"
							identifier={component.identifier}
							name={component.name}
							prefix="on"
							timeRange={timeRange}
							timestamp={component.timestamp ?? timestamp}
							type={component.type}
						/>
					)
				}
				size="sm"
				title={
					title && (
						<Truncated
							className="font-medium text-grey-800"
							tooltip={description ? `${title} - ${description}` : undefined}
						>
							{title}
						</Truncated>
					)
				}
			>
				{actions}
			</WidgetHeader>

			<WidgetBody>{children}</WidgetBody>
		</Widget>
	);
};
